import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChartNoAxesColumn, ChevronRight, X } from 'lucide-react';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import { balanceState, type RadarUsageBalance, type RadarUsageContext, type RadarUsageMetric } from './radarUsageModel';
import '@/styles/radar-usage.css';
import {RadarBillingPanel} from './RadarBillingPanel';

const metrics: RadarUsageMetric[] = ['companies', 'emails', 'drafts', 'monitoring'];
const labels = {companies:'Company discovery',emails:'Companies with email',monitoring:'Monitored companies',drafts:'Outreach drafts'};
const descriptions = {
  companies: 'Only new, matching companies delivered to your workspace count. Repeats and empty results do not.',
  emails: 'One credit per company with a sourced business email or a verified contact email. Verification status is shown with each contact. Failed lookups and repeat views are free.',
  monitoring: 'Active monitoring slots. Pausing a company releases its slot.',
  drafts: 'Counted when a new draft is saved. Viewing, manual editing and export do not count.',
};

function hasExpired(data: RadarUsageContext) {
  const expiry = data.outcome?.expires_at || data.access?.expires_at;
  return data.outcome?.status === 'expired' || !!expiry && Date.parse(expiry) <= Date.now();
}

function remainingCopy(metric: RadarUsageMetric, expired: boolean) {
  if (metric === 'drafts') return expired ? '{count} drafts unused' : '{count} drafts available';
  if (metric === 'emails') return expired ? '{count} companies with email unused' : '{count} companies with email available';
  if (metric === 'monitoring') return expired ? '{count} monitoring slots unused' : '{count} monitoring slots available';
  return expired ? '{count} companies unused' : '{count} companies available';
}

export function RadarUsageEntry({data, onOpen}: {data: RadarUsageContext; onOpen: () => void}) {
  const {t} = useRadarLocale();
  return <button type="button" className="radar-usage-entry" onClick={onOpen} aria-haspopup="dialog">
    <ChartNoAxesColumn size={17} aria-hidden="true"/>
    <span><strong>{t('Usage & plan')}</strong><small>{t(data.outcome?.mode==='outcome_subscription' ? (hasExpired(data)?'Plan inactive':'Basic') : hasExpired(data) ? 'Trial ended' : data.outcome ? 'Free trial' : 'Pilot workspace')}</small></span>
    <ChevronRight size={15} aria-hidden="true"/>
  </button>;
}

function Meter({label, balance, active=false, expired=false}: {
  label: string; balance: RadarUsageBalance; active?: boolean; expired?: boolean;
}) {
  const {t} = useRadarLocale();
  const usedPercent = balance.limit > 0 ? Math.min(100, Math.max(0, balance.used / balance.limit * 100)) : 0;
  const reservedPercent = balance.limit > 0 ? Math.min(100-usedPercent, Math.max(0, balance.reserved / balance.limit * 100)) : 0;
  return <div className="radar-usage-meter" aria-label={t(label)} data-state={expired ? 'expired' : balanceState(balance)}>
    <div className="radar-usage-meter-top"><strong>{t(label)}</strong><span>{t(active ? '{used} / {limit} active' : '{used} / {limit} used', {used:balance.used,limit:balance.limit})}</span></div>
    <div className="radar-usage-value" aria-hidden="true"><strong>{active ? balance.used : balance.remaining}</strong><span>{t(active ? 'Monitoring now' : expired ? 'Unused' : 'Remaining')}</span></div>
    <div className="radar-usage-track" aria-hidden="true"><span style={{width:`${usedPercent}%`}}/><i style={{width:`${reservedPercent}%`}}/></div>
  </div>;
}

export function RadarUsageInline({data, page, onOpen}: {
  data: RadarUsageContext; page: 'brief'|'discover'|'watch'|'act'; onOpen: () => void;
}) {
  const {t} = useRadarLocale();
  const expired = hasExpired(data);
  const relevant: RadarUsageMetric[] = page==='discover' ? ['companies'] : page==='watch' ? ['monitoring'] : page==='act' ? ['emails','drafts'] : [];
  let content: ReactNode = null;
  if (data.outcome && relevant.length) {
    content = relevant.map(metric => {
      const balance = data.outcome!.balances[metric];
      const state = balanceState(balance);
      return <span key={metric} data-state={expired?'expired':state}>
        {t(remainingCopy(metric, expired), {count:balance.remaining})}
        {!expired && state==='low' && <small>{t('Running low')}</small>}
        {!expired && state==='empty' && <small>{t(balance.reserved ? 'Work in progress' : 'Allowance used')}</small>}
      </span>;
    });
  } else if (page==='discover' && typeof data.searchesRemaining==='number') {
    content = <span data-state={data.searchesRemaining===0?'empty':'normal'}>{t('{count} searches remaining', {count:data.searchesRemaining})}</span>;
  } else if (page==='watch' && data.monitoring) {
    content = <span>{t('{used} / {limit} monitoring slots active', {used:data.monitoring.active_count,limit:data.monitoring.limit})}</span>;
  }
  if (!content) return null;
  return <div className="radar-usage-inline"><div aria-live="polite">{content}</div>
    <button type="button" onClick={onOpen} aria-haspopup="dialog">{t('View usage')}<ChevronRight size={13} aria-hidden="true"/></button>
  </div>;
}

function UsageOverview({data,onReview}: {data:RadarUsageContext;onReview?:()=>void}) {
  const {t,locale}=useRadarLocale();
  const [detailOpen,setDetailOpen]=useState(false);
  const expired=hasExpired(data);
  const outcome=data.outcome;
  const paid=outcome?.mode==='outcome_subscription';
  const expiry=outcome?.expires_at || data.access?.expires_at;
  const formatDate=(date:string)=>new Intl.DateTimeFormat(locale,{year:'numeric',month:'short',day:'numeric'}).format(new Date(date));
  const validDate=(date?:string|null):date is string=>!!date && Number.isFinite(Date.parse(date));
  const known=!!outcome || typeof data.searchesRemaining==='number' || !!data.monitoring;
  return <>
    <div className="radar-usage-overview" hidden={detailOpen}>
    <section className="radar-usage-period">
      <div><strong>{t(paid?'Basic':outcome?'Free trial':'Pilot workspace')}</strong>{expired && <span>{t(paid?'Plan inactive':'Trial ended')}</span>}</div>
      {outcome && validDate(outcome.starts_at) && validDate(outcome.expires_at)
        ? <p>{formatDate(outcome.starts_at)} — {formatDate(outcome.expires_at)}</p>
        : validDate(expiry) && <p>{t('Access ends {date}',{date:formatDate(expiry)})}</p>}
      <p>{t('Workspace usage')}</p>
      {paid && <p>{t(outcome.billing?.cancel_at_period_end?'Cancels at the end of this period.':'Allowances renew after a successful monthly payment.')}</p>}
      {paid && outcome.billing?.subscription_status==='past_due' && <p>{t('Payment needs attention. Manage your subscription to update payment details.')}</p>}
    </section>
    {!known && <p role="status" className="radar-usage-unavailable">{t('Usage is unavailable. Refresh the workspace to try again.')}</p>}
    {outcome ? <div className="radar-usage-balances radar-usage-outcomes">{metrics.map(metric=>{
      const balance=outcome.balances[metric];
      return <section className="radar-usage-balance" key={metric}>
        <Meter label={labels[metric]} balance={balance} active={metric==='monitoring'} expired={expired}/>
        <div className="radar-usage-balance-note"><span>{t(remainingCopy(metric,expired),{count:balance.remaining})}</span>
          {balance.reserved>0 && <small>{t('{count} reserved for work in progress',{count:balance.reserved})}</small>}
        </div>
      </section>;
    })}</div> : <div className="radar-usage-balances">
      {typeof data.searchesRemaining==='number' && <section className="radar-usage-balance radar-usage-legacy">
        <strong>{t('Discovery searches')}</strong><span>{t('{count} searches remaining',{count:data.searchesRemaining})}</span>
        <p>{t('Your current pilot allowance is measured in searches. One search can return multiple companies.')}</p>
      </section>}
      {data.monitoring && <section className="radar-usage-balance">
        <Meter label="Monitored companies" balance={{limit:data.monitoring.limit,used:data.monitoring.active_count,reserved:0,remaining:Math.max(0,data.monitoring.limit-data.monitoring.active_count)}} active expired={expired}/>
        <p>{t('Active monitoring slots. Pausing a company releases its slot.')}</p>
      </section>}
    </div>}
    {data.refreshFailed && <p role="status" className="radar-usage-unavailable">{t('Could not refresh usage. These are the last confirmed balances.')}</p>}
    {outcome?.feedback && <div className="radar-usage-feedback">
      <p>{t(outcome.feedback.grant_status==='granted' ? 'Feedback bonus included in these balances.' : '{completed} / {required} company reviews completed.',{completed:Math.min(outcome.feedback.completed,outcome.feedback.required),required:outcome.feedback.required})}</p>
      {!expired && outcome.feedback.grant_status!=='granted' && <>
        <p>{t('Review 10 different companies to receive 100 more companies, 20 company emails and 20 drafts. Positive and negative feedback both count.')}</p>
        {onReview && <button type="button" onClick={onReview}>{t('Review companies')}<ChevronRight size={13} aria-hidden="true"/></button>}
      </>}
    </div>}
    </div>
    {data.billing && <RadarBillingPanel key={data.billing.token+'/'+data.billing.workspaceId} connection={data.billing} outcome={outcome} onDetailChange={setDetailOpen}/>}
    {known && <details hidden={detailOpen} className="radar-usage-rules"><summary>{t(paid?'Counting rules':outcome?'Trial & counting rules':'Allowance information')}</summary>
      {outcome ? <><p>{t(paid?'Allowances renew after each monthly payment. Unused allowances do not roll over.':'Allowances cover the entire trial period; they do not reset daily.')}</p>{metrics.map(metric=><p key={metric}>{t(descriptions[metric])}</p>)}<p>{t('Reservations are held while work runs. Only delivered results are charged; unused reservations are released.')}</p></>
        : <p>{t('These are the allowances currently granted to your account. Changes to result-based plans will be shown here when available.')}</p>}
    </details>}
  </>;
}

function UsageDialog({data,onClose,onReview}: {data:RadarUsageContext;onClose:()=>void;onReview?:()=>void}) {
  const {t}=useRadarLocale();
  const ref=useRef<HTMLDialogElement>(null);
  const titleId=useId();
  useEffect(()=>{
    const dialog=ref.current;
    const previous=document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialog) return;
    const overflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open','');
    return ()=>{document.body.style.overflow=overflow;if(dialog.open && dialog.close)dialog.close();if(previous?.isConnected)previous.focus();};
  },[]);
  return createPortal(<dialog ref={ref} className="radar-usage-drawer" aria-labelledby={titleId}
    onCancel={event=>{event.preventDefault();onClose();}}
    onKeyDown={event=>{
      if(event.key!=='Tab')return;
      const controls=Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled),summary,a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled)')).filter(control=>!control.closest('[hidden]'));
      const first=controls[0],last=controls[controls.length-1];
      if(event.shiftKey && document.activeElement===first){event.preventDefault();last?.focus();}
      else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first?.focus();}
    }}
    onClick={event=>{if(event.target===event.currentTarget){const bounds=event.currentTarget.getBoundingClientRect();if(event.clientX<bounds.left || event.clientX>bounds.right || event.clientY<bounds.top || event.clientY>bounds.bottom)onClose();}}}>
    <header><h2 id={titleId}>{t('Usage & plan')}</h2><button type="button" aria-label={t('Close usage')} onClick={onClose}><X size={19}/></button></header>
    <div className="radar-usage-body"><UsageOverview data={data} onReview={onReview}/></div>
  </dialog>,document.body);
}

export function RadarUsageDrawer({open,...props}: {open:boolean;data:RadarUsageContext;onClose:()=>void;onReview?:()=>void}) {
  return open ? <UsageDialog {...props} onClose={()=>{
    props.data.billing?.onReturnDismissed?.();props.onClose();
  }} onReview={props.onReview ? ()=>{
    props.data.billing?.onReturnDismissed?.();props.onReview?.();
  } : undefined}/> : null;
}
